import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApiClient } from "../utils/apiClient";
import { AuthContext } from "../contexts/AuthContext";
import {
    PlusIcon, XMarkIcon, DocumentIcon, ArrowUturnLeftIcon,
    ChatBubbleLeftRightIcon,
    ExclamationTriangleIcon, ArrowsUpDownIcon, MagnifyingGlassIcon,
    ChevronRightIcon, ChevronDownIcon, WrenchScrewdriverIcon,
    ArrowPathIcon, FlagIcon,
} from "@heroicons/react/24/outline";

// ---------- date/time helpers ----------
// ASCAA uses two timestamp formats:
//   S3 last_modified → ISO with colons (JS Date parses natively)
//   metadata/key timestamps → "YYYY-MM-DDTHH-MM-SS-microZ" (dashes, unparseable)
const parseTs = (s) => {
    if (!s) return null;
    const m = /^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d+)Z?$/.exec(s);
    if (m) {
        const ms = (m[5] || "0").padStart(6, "0").slice(0, 3);
        return new Date(`${m[1]}T${m[2]}:${m[3]}:${m[4]}.${ms}Z`);
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (v) => {
    const d = v instanceof Date ? v : parseTs(v);
    return d ? d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    }) : "—";
};

const fmtTime = (v) => {
    const d = v instanceof Date ? v : parseTs(v);
    return d ? d.toLocaleTimeString(undefined, {
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    }) : "—";
};

const fmtDuration = (ms, prefix = "") => {
    if (ms == null || isNaN(ms) || ms < 0) return "";
    if (ms < 1000) return `${prefix}${ms}ms`;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${prefix}${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${prefix}${m}m ${s % 60}s`;
    const h = Math.floor(m / 60);
    return `${prefix}${h}h ${m % 60}m`;
};

const fmtRelDelta = (fromDate, toDate) => {
    if (!fromDate || !toDate) return "";
    return fmtDuration(toDate - fromDate, "+");
};

const fmtBytes = (n) => {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// ---------- key parsing ----------
// key format: workflows/{id}/calls/{utc_ts}_{tool}.json  or  .../logs/{utc_ts}.md
const parseKey = (key) => {
    const fname = (key.split("/").pop() || "").replace(/\.json$|\.md$/, "");
    // ts is the leading dashed timestamp; split on the FIRST "_" AFTER the trailing Z
    const zi = fname.indexOf("Z");
    if (zi > 0) {
        const ts = fname.slice(0, zi + 1);
        const rest = fname.slice(zi + 1).replace(/^_/, "");
        return { ts, tool: rest || null };
    }
    return { ts: fname, tool: null };
};

// ---------- workflow event building ----------
const buildEvents = (detail) => {
    if (!detail) return [];
    const events = [];

    const wfCreated = parseTs(detail.workflow?.created_at);
    events.push({
        id: `wf:${detail.workflow.workflow_id}`,
        type: "workflow",
        ts: wfCreated,
        primary: "Workflow created",
        secondary: detail.workflow.created_by,
    });

    for (const c of detail.calls || []) {
        const { ts, tool } = parseKey(c.key);
        const isError = /_error/.test(c.key);
        events.push({
            id: `call:${c.key}`,
            type: "call",
            key: c.key,
            ts: parseTs(ts),
            primary: tool || "(unknown tool)",
            size: c.size,
            isError,
        });
    }
    for (const l of detail.logs || []) {
        const { ts } = parseKey(l.key);
        events.push({
            id: `log:${l.key}`,
            type: "log",
            key: l.key,
            ts: parseTs(ts),
            primary: "log entry",
            size: l.size,
        });
    }
    for (const f of detail.files || []) {
        events.push({
            id: `file:${f.path}`,
            type: "file",
            path: f.path,
            ts: parseTs(f.last_modified),
            primary: f.path,
            size: f.size,
        });
    }
    return events;
};

// ---------- type styling ----------
const TYPE_META = {
    workflow: { label: "workflow", icon: FlagIcon,
        chip: "bg-purple-100 text-purple-700",
        dot: "bg-purple-500", ring: "ring-purple-200" },
    call:     { label: "tool",     icon: WrenchScrewdriverIcon,
        chip: "bg-blue-100 text-blue-700",
        dot: "bg-blue-500", ring: "ring-blue-200" },
    log:      { label: "log",      icon: ChatBubbleLeftRightIcon,
        chip: "bg-amber-100 text-amber-700",
        dot: "bg-amber-500", ring: "ring-amber-200" },
    file:     { label: "file",     icon: DocumentIcon,
        chip: "bg-emerald-100 text-emerald-700",
        dot: "bg-emerald-500", ring: "ring-emerald-200" },
};

// ---------- inline expanded body ----------
function CallBody({ data }) {
    if (!data) return <div className="text-xs text-gray-500">No content.</div>;
    const req = data.request;
    const resp = data.response;
    const err = data.error;
    return (
        <div className="space-y-3">
            <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Request</div>
                <pre className="bg-gray-900 text-gray-100 rounded px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
{typeof req === "string" ? req : JSON.stringify(req, null, 2)}
                </pre>
            </div>
            {err && (
                <div>
                    <div className="text-[11px] uppercase tracking-wide text-red-600 mb-1">Error</div>
                    <pre className="bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
{typeof err === "string" ? err : JSON.stringify(err, null, 2)}
                    </pre>
                </div>
            )}
            {!err && (
                <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Response</div>
                    <pre className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
{typeof resp === "string" ? resp : JSON.stringify(resp, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
}

function TextBody({ text }) {
    return (
        <pre className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
{text || "(empty)"}
        </pre>
    );
}

// ---------- main ----------
export default function Workflows({ embedded = false }) {
    const apiClient = useApiClient();
    const { user } = useContext(AuthContext);
    const isManager = user?.activeRole === "Manager";

    const { workflowId: routeWorkflowId } = useParams();
    const navigate = useNavigate();

    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [nextOffset, setNextOffset] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const PAGE_SIZE = 20;
    const selectedId = routeWorkflowId || null;
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [uploader, setUploader] = useState(null);

    const [expanded, setExpanded] = useState({}); // id → true
    const [bodies, setBodies] = useState({});     // id → content
    const [loadingBody, setLoadingBody] = useState({}); // id → true
    const [query, setQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState({ call: true, log: true, file: true, workflow: true });
    const [sortAsc, setSortAsc] = useState(true);

    async function fetchList(offset = 0, append = false) {
        if (append) setLoadingMore(true); else setLoading(true);
        setError("");
        try {
            const resp = await apiClient.get(`/workflows?limit=${PAGE_SIZE}&offset=${offset}`);
            const rows = resp.workflows || [];
            setList((prev) => (append ? [...prev, ...rows] : rows));
            setTotal(resp.total ?? rows.length);
            setNextOffset(resp.next_offset ?? null);
        } catch { setError("Failed to load workflows"); }
        finally { if (append) setLoadingMore(false); else setLoading(false); }
    }

    async function fetchDetail(wid) {
        setLoading(true); setError("");
        setExpanded({}); setBodies({}); setLoadingBody({});
        try {
            const resp = await apiClient.get(`/workflows?workflow_id=${encodeURIComponent(wid)}`);
            setDetail(resp);
        } catch { setError(`Failed to load workflow ${wid}`); }
        finally { setLoading(false); }
    }

    useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, []);

    // When route param changes (initial load or back/forward), load detail.
    useEffect(() => {
        if (routeWorkflowId) {
            fetchDetail(routeWorkflowId);
        } else {
            setDetail(null);
        }
        /* eslint-disable-next-line */
    }, [routeWorkflowId]);

    async function toggleEvent(ev) {
        const id = ev.id;
        const isOpen = !!expanded[id];
        setExpanded((s) => ({ ...s, [id]: !isOpen }));
        if (isOpen || bodies[id] || ev.type === "workflow") return;

        setLoadingBody((s) => ({ ...s, [id]: true }));
        try {
            if (ev.type === "call") {
                const resp = await apiClient.get(
                    `/workflows?workflow_id=${encodeURIComponent(selectedId)}&call=${encodeURIComponent(ev.key)}`);
                setBodies((s) => ({ ...s, [id]: { kind: "call", data: resp.call } }));
            } else if (ev.type === "log") {
                const resp = await apiClient.get(
                    `/workflows?workflow_id=${encodeURIComponent(selectedId)}&log=${encodeURIComponent(ev.key)}`);
                setBodies((s) => ({ ...s, [id]: { kind: "text", text: resp.log?.content || "" } }));
            } else if (ev.type === "file") {
                const resp = await apiClient.get(
                    `/workflows?workflow_id=${encodeURIComponent(selectedId)}&file=${encodeURIComponent(ev.path)}`);
                setBodies((s) => ({ ...s, [id]: { kind: "text", text: resp.file?.content || "" } }));
            }
        } catch {
            setError(`Failed to load ${ev.type}`);
        } finally {
            setLoadingBody((s) => ({ ...s, [id]: false }));
        }
    }

    async function uploadFile() {
        if (!uploader?.path.trim()) { setError("Path required"); return; }
        try {
            await apiClient.post(
                `/workflows?workflow_id=${encodeURIComponent(selectedId)}&file=${encodeURIComponent(uploader.path.trim())}`,
                { content: uploader.content });
            setUploader(null);
            await fetchDetail(selectedId);
        } catch { setError(`Failed to upload ${uploader.path}`); }
    }

    const events = useMemo(() => buildEvents(detail), [detail]);
    const filteredEvents = useMemo(() => {
        const q = query.trim().toLowerCase();
        let arr = events.filter((e) => typeFilter[e.type]);
        if (q) arr = arr.filter((e) => (e.primary || "").toLowerCase().includes(q));
        arr.sort((a, b) => {
            const ta = a.ts ? a.ts.getTime() : 0;
            const tb = b.ts ? b.ts.getTime() : 0;
            return sortAsc ? ta - tb : tb - ta;
        });
        return arr;
    }, [events, query, typeFilter, sortAsc]);

    const workflowStart = detail ? parseTs(detail.workflow?.created_at) : null;
    const counts = useMemo(() => {
        const c = { call: 0, log: 0, file: 0 };
        for (const e of events) if (c[e.type] != null) c[e.type]++;
        return c;
    }, [events]);

    // Chronological (asc) for duration math; also captures first/last real events
    const chronoEvents = useMemo(() => {
        return [...events]
            .filter((e) => e.ts)
            .sort((a, b) => a.ts - b.ts);
    }, [events]);

    // ev.id → duration since previous chrono event (ms). Excludes the "workflow" seed event.
    const durationById = useMemo(() => {
        const map = {};
        for (let i = 1; i < chronoEvents.length; i++) {
            map[chronoEvents[i].id] = chronoEvents[i].ts - chronoEvents[i - 1].ts;
        }
        return map;
    }, [chronoEvents]);

    const firstTs = chronoEvents.length > 0 ? chronoEvents[0].ts : null;
    const lastTs  = chronoEvents.length > 0 ? chronoEvents[chronoEvents.length - 1].ts : null;
    const totalMs = firstTs && lastTs ? lastTs - firstTs : null;

    // ---------- LIST VIEW ----------
    if (!selectedId) {
        return (
            <div className={embedded ? "" : "p-6 max-w-6xl mx-auto"}>
                <div className="flex justify-between items-start mb-6">
                    <div>
                        {!embedded && <h1 className="text-2xl font-bold text-gray-900">Workflows</h1>}
                        <p className="text-sm text-gray-500 mt-1">
                            Every MCP tool call is grouped by workflow_id. Workflows are created by agents via the <span className="font-mono">create_workflow</span> MCP tool. Reports written by <span className="font-mono">add_run_report</span> land here as workflow files. Click one to see its timeline.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => fetchList()}
                            className="p-2 rounded-md border border-gray-300 hover:bg-gray-50" title="Refresh">
                            <ArrowPathIcon className="h-5 w-5 text-gray-600" />
                        </button>
                    </div>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

                {loading ? (
                    <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                ) : list.length === 0 ? (
                    <div className="bg-white shadow rounded-lg px-6 py-8 text-center text-gray-500">
                        No workflows yet. Ask an agent to call the <span className="font-mono">create_workflow</span> MCP tool.
                    </div>
                ) : (
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">workflow_id</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {list.map((w) => (
                                    <tr key={w.workflow_id}
                                        className="hover:bg-blue-50 cursor-pointer"
                                        onClick={() => navigate(`/workflows/${encodeURIComponent(w.workflow_id)}`)}>
                                        <td className="px-6 py-3 text-sm text-gray-900 max-w-md truncate">
                                            {w.description || <span className="text-gray-400">(untitled)</span>}
                                        </td>
                                        <td className="px-6 py-3 text-xs text-gray-500 font-mono">{w.workflow_id}</td>
                                        <td className="px-6 py-3 text-sm text-gray-500">{fmtDate(w.created_at)}</td>
                                        <td className="px-6 py-3 text-sm text-gray-600 truncate max-w-xs"
                                            title={w.created_by || ""}>
                                            {w.created_by_name || w.created_by || "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 text-sm text-gray-500">
                            <span>Showing {list.length} of {total}</span>
                            {nextOffset != null && (
                                <button onClick={() => fetchList(nextOffset, true)} disabled={loadingMore}
                                    className="px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700 disabled:opacity-50">
                                    {loadingMore ? "Loading…" : "Load more"}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ---------- DETAIL VIEW ----------
    return (
        <div className="p-6 max-w-5xl mx-auto">
            <button className="mb-4 flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900"
                onClick={() => navigate("/agent-files?tab=workflows")}>
                <ArrowUturnLeftIcon className="h-4 w-4" /> Back to workflows
            </button>

            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

            {!detail ? (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : (
                <>
                    {/* Header card */}
                    <div className="bg-white shadow rounded-lg p-6 mb-4">
                        <h1 className="text-xl font-semibold text-gray-900 leading-snug">
                            {detail.workflow.description || <span className="text-gray-400">(untitled)</span>}
                        </h1>
                        <div className="text-xs text-gray-500 font-mono mt-1 truncate">{detail.workflow.workflow_id}</div>

                        {/* Timeline summary strip */}
                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">First event</div>
                                <div className="text-sm text-gray-900 mt-0.5">{firstTs ? fmtDate(firstTs) : "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Last event</div>
                                <div className="text-sm text-gray-900 mt-0.5">{lastTs ? fmtDate(lastTs) : "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Total elapsed</div>
                                <div className="text-sm font-semibold text-gray-900 mt-0.5">
                                    {totalMs != null ? fmtDuration(totalMs) : "—"}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-4 text-xs">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-700">
                                <WrenchScrewdriverIcon className="h-3.5 w-3.5" /> {counts.call} tool calls
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-700">
                                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" /> {counts.log} logs
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-700">
                                <DocumentIcon className="h-3.5 w-3.5" /> {counts.file} files
                            </span>
                            {detail.workflow.created_by && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-gray-600 font-mono">
                                    {detail.workflow.created_by}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="bg-white shadow rounded-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                            <input
                                type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                                placeholder="Filter events…"
                                className="flex-1 text-sm border-0 focus:ring-0 focus:outline-none bg-transparent"
                            />
                        </div>
                        {["call", "log", "file"].map((t) => {
                            const meta = TYPE_META[t];
                            const on = typeFilter[t];
                            return (
                                <button key={t}
                                    onClick={() => setTypeFilter((s) => ({ ...s, [t]: !s[t] }))}
                                    className={`text-xs px-2 py-1 rounded border ${on ? meta.chip + " border-transparent" : "bg-white border-gray-300 text-gray-400"}`}>
                                    {meta.label}s
                                </button>
                            );
                        })}
                        <button
                            onClick={() => setSortAsc((v) => !v)}
                            className="text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-50 flex items-center gap-1 text-gray-600"
                            title="Toggle sort direction">
                            <ArrowsUpDownIcon className="h-3.5 w-3.5" />
                            {sortAsc ? "oldest first" : "newest first"}
                        </button>
                        {isManager && (
                            <button onClick={() => setUploader({ path: "", content: "" })}
                                className="text-xs px-2 py-1 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1">
                                <PlusIcon className="h-3.5 w-3.5" /> file
                            </button>
                        )}
                    </div>

                    {/* Timeline */}
                    {filteredEvents.length === 0 ? (
                        <div className="bg-white shadow rounded-lg px-6 py-12 text-center text-gray-500 text-sm">
                            No events match your filters.
                        </div>
                    ) : (
                        <div className="relative pl-8">
                            {/* vertical line */}
                            <div className="absolute left-3 top-2 bottom-2 w-px bg-gray-200" aria-hidden />

                            <ol className="space-y-3">
                                {filteredEvents.map((ev, idx) => {
                                    const meta = TYPE_META[ev.type];
                                    const Icon = meta.icon;
                                    const isOpen = !!expanded[ev.id];
                                    const body = bodies[ev.id];
                                    const isLoading = !!loadingBody[ev.id];
                                    const rel = fmtRelDelta(workflowStart, ev.ts);
                                    const took = durationById[ev.id] != null ? fmtDuration(durationById[ev.id]) : "";
                                    return (
                                        <li key={ev.id} className="relative">
                                            {/* dot */}
                                            <span className={`absolute -left-[22px] top-3 h-3 w-3 rounded-full ${meta.dot} ring-4 ${meta.ring}`} />

                                            <div className={`bg-white shadow-sm border rounded-lg overflow-hidden ${ev.isError ? "border-red-300" : "border-gray-200"}`}>
                                                <button
                                                    onClick={() => toggleEvent(ev)}
                                                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
                                                    {isOpen
                                                        ? <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                        : <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                                                    <Icon className={`h-4 w-4 flex-shrink-0 ${ev.isError ? "text-red-500" : "text-gray-500"}`} />
                                                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.chip} flex-shrink-0`}>
                                                        {meta.label}
                                                    </span>
                                                    <span className="text-sm font-medium text-gray-900 truncate flex-1">{ev.primary}</span>
                                                    {ev.isError && (
                                                        <span className="inline-flex items-center gap-1 text-xs text-red-600 flex-shrink-0">
                                                            <ExclamationTriangleIcon className="h-3.5 w-3.5" /> error
                                                        </span>
                                                    )}
                                                    {ev.size != null && (
                                                        <span className="text-xs text-gray-400 flex-shrink-0">{fmtBytes(ev.size)}</span>
                                                    )}
                                                    {took && (
                                                        <span
                                                            className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono flex-shrink-0"
                                                            title="Time since previous event">
                                                            {took}
                                                        </span>
                                                    )}
                                                    {rel && (
                                                        <span className="text-xs text-gray-400 font-mono flex-shrink-0 hidden sm:inline" title={`Elapsed since workflow start · ${fmtDate(ev.ts)}`}>{rel}</span>
                                                    )}
                                                    <span className="text-xs text-gray-500 font-mono flex-shrink-0" title={fmtDate(ev.ts)}>{fmtTime(ev.ts)}</span>
                                                </button>

                                                {isOpen && (
                                                    <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3">
                                                        {isLoading ? (
                                                            <div className="text-xs text-gray-500 flex items-center gap-2">
                                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />
                                                                Loading…
                                                            </div>
                                                        ) : ev.type === "workflow" ? (
                                                            <div className="text-xs text-gray-600">
                                                                Workflow started at {fmtDate(ev.ts)}
                                                                {detail.workflow.created_by && <> by <span className="font-mono">{detail.workflow.created_by}</span></>}.
                                                            </div>
                                                        ) : body?.kind === "call" ? (
                                                            <CallBody data={body.data} />
                                                        ) : body?.kind === "text" ? (
                                                            <TextBody text={body.text} />
                                                        ) : (
                                                            <div className="text-xs text-gray-500">No content.</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    )}
                </>
            )}

            {uploader && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded shadow flex flex-col">
                        <div className="flex-shrink-0 p-6 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-lg font-bold">Upload file</h2>
                            <button className="p-1 rounded hover:bg-gray-100" onClick={() => setUploader(null)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Path</label>
                                <input type="text" value={uploader.path}
                                    onChange={(e) => setUploader({ ...uploader, path: e.target.value })}
                                    className="w-full border rounded px-3 py-2 font-mono text-sm"
                                    placeholder="e.g. plan.md or notes/context.txt" autoFocus />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Content</label>
                                <textarea value={uploader.content}
                                    onChange={(e) => setUploader({ ...uploader, content: e.target.value })}
                                    rows={18} className="w-full border rounded px-3 py-2 font-mono text-sm" />
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex justify-end gap-2 p-6 border-t border-gray-200">
                            <button className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded" onClick={() => setUploader(null)}>Cancel</button>
                            <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded" onClick={uploadFile}>Upload</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
